/**
 * The user's own account: profile, password, and where they are signed in.
 *
 * Everything here is self-service, and that is why none of it carries a
 * permission check. Requiring one to edit your own profile would mean an
 * administrator could lock somebody out of their own name.
 */

import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { z } from "zod";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  Input,
  Page,
  useToast,
  type Column,
} from "@hardauto/core-ui";
import { Field, applyServerErrors } from "@hardauto/core-forms";
import { ApiError, NetworkError, type ApiClient } from "@hardauto/core-api";

import { useSession } from "../session/session";
import { NAMESPACE } from "../i18n";

/** i18next's own function type.
 *
 * A hand-rolled `(key: string, options?: object) => string` cannot express its
 * overloads under `exactOptionalPropertyTypes`, and writing one that almost
 * fits produces an error at every call site rather than a useful abstraction. */
type Translate = TFunction<typeof NAMESPACE>;

function describe(error: unknown, t: Translate): string {
  if (error instanceof NetworkError) return t("error.offline");
  if (!(error instanceof ApiError)) return t("error.unexpected");
  if (error.type === "rate_limited") {
    return t("error.rateLimited", { seconds: error.retryAfterSeconds ?? 60 });
  }
  return error.message;
}

const profileSchema = z.object({
  display_name: z.string().max(200).optional(),
  locale: z.string().max(16).optional(),
  timezone: z.string().max(64).optional(),
});

type ProfileValues = z.infer<typeof profileSchema>;

const passwordSchema = z.object({
  current_password: z.string().min(1),
  new_password: z.string().min(1),
});

type PasswordValues = z.infer<typeof passwordSchema>;

/** One entry in the session list, as the API describes it. */
interface SessionRow {
  id: string;
  device_label: string | null;
  last_used_at: string;
  created_from_location: string | null;
  created_from_ip: string | null;
  is_current: boolean;
}

export function AccountScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const session = useSession();

  return (
    <Page title={t("account.title")} description={t("account.description")}>
      {session.user && !session.user.email_verified_at ? (
        <Alert tone="warning">{t("account.emailNotVerified")}</Alert>
      ) : null}

      <ProfileCard client={client} />
      <PasswordCard client={client} />
      <SessionsCard client={client} />
    </Page>
  );
}

function ProfileCard({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const session = useSession();
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    values: {
      display_name: session.user?.display_name ?? "",
      locale: session.user?.locale ?? "",
      timezone: session.user?.timezone ?? "",
    },
  });

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await client.request(client.authenticatedPath("/me"), { method: "PATCH", body: values });
      // Re-read rather than trusting the local copy: the server may normalise
      // what was sent, and a screen showing a value the server did not store
      // is a screen that lies until the next reload.
      await session.refreshUser();
      toast.show(t("account.profileSaved"), { tone: "success" });
    } catch (error) {
      setFormError(describe(error, t));
      if (error instanceof ApiError) {
        applyServerErrors(error, form.setError, ["display_name", "locale", "timezone"]);
      }
    }
  });

  return (
    <Card title={t("account.profile")}>
      <form onSubmit={(event) => void submit(event)} noValidate>
        {formError ? <Alert tone="danger">{formError}</Alert> : null}

        <Field label={t("field.email")}>
          {(props) => (
            // Read-only: changing an address is a verified flow of its own,
            // not a profile edit, because the new address must be proved
            // before the old one stops working.
            <Input {...props} value={session.user?.email ?? ""} readOnly />
          )}
        </Field>

        <Field label={t("field.displayName")} error={form.formState.errors.display_name?.message}>
          {(props) => <Input {...props} {...form.register("display_name")} autoComplete="name" />}
        </Field>

        <Button
          type="submit"
          variant="primary"
          busy={form.formState.isSubmitting}
          busyLabel={t("account.savingProfile")}
        >
          {t("account.saveProfile")}
        </Button>
      </form>
    </Card>
  );
}

function PasswordCard({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<PasswordValues>({ resolver: zodResolver(passwordSchema) });

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await client.request(client.authenticatedPath("/password-change"), {
        method: "POST",
        body: values,
      });
      form.reset({ current_password: "", new_password: "" });
      // Says out loud that other devices were signed out. The server does that
      // on a password change — the usual reason for changing one is that
      // somebody else may have it — and a user who is not told will be
      // surprised on their phone an hour later.
      toast.show(t("account.passwordChanged"), { tone: "success" });
    } catch (error) {
      setFormError(describe(error, t));
      if (error instanceof ApiError) {
        applyServerErrors(error, form.setError, ["current_password", "new_password"]);
      }
    }
  });

  return (
    <Card title={t("account.password")}>
      <form onSubmit={(event) => void submit(event)} noValidate>
        {formError ? <Alert tone="danger">{formError}</Alert> : null}

        <Field
          label={t("field.currentPassword")}
          error={form.formState.errors.current_password?.message}
          required
        >
          {(props) => (
            <Input
              {...props}
              {...form.register("current_password")}
              type="password"
              autoComplete="current-password"
            />
          )}
        </Field>

        <Field
          label={t("field.newPassword")}
          error={form.formState.errors.new_password?.message}
          required
        >
          {(props) => (
            <Input
              {...props}
              {...form.register("new_password")}
              type="password"
              autoComplete="new-password"
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          busy={form.formState.isSubmitting}
          busyLabel={t("account.changingPassword")}
        >
          {t("account.changePassword")}
        </Button>
      </form>
    </Card>
  );
}

function SessionsCard({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [pending, setPending] = useState<SessionRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const data = await client.request<{ sessions: SessionRow[] }>(
        client.authenticatedPath("/sessions"),
      );
      setRows(data.sessions);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (row: SessionRow) => {
    await client.request(client.authenticatedPath(`/sessions/${row.id}`), { method: "DELETE" });
    setPending(null);
    toast.show(t("sessions.revoked"), { tone: "success" });
    await load();
  };

  const columns: Column<SessionRow>[] = [
    {
      key: "device",
      header: t("sessions.device"),
      render: (row) => (
        <>
          {row.device_label ?? "—"}{" "}
          {/* Marked, because "sign out everything" is only safe to press when
              you can see which row is the machine you are holding. */}
          {row.is_current ? <Badge tone="info">{t("sessions.current")}</Badge> : null}
        </>
      ),
    },
    {
      key: "location",
      header: t("sessions.location"),
      secondary: true,
      render: (row) => row.created_from_location ?? row.created_from_ip ?? "—",
    },
    {
      key: "lastUsed",
      header: t("sessions.lastUsed"),
      secondary: true,
      render: (row) => new Date(row.last_used_at).toLocaleString(),
    },
    {
      key: "actions",
      header: "",
      align: "end",
      render: (row) =>
        row.is_current ? null : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setPending(row);
            }}
          >
            {t("sessions.revoke")}
          </Button>
        ),
    },
  ];

  return (
    <Card title={t("sessions.title")} description={t("sessions.description")}>
      <DataTable<SessionRow>
        caption={t("sessions.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        error={
          failed
            ? {
                title: t("sessions.loadFailed"),
                onRetry: () => void load(),
                retryLabel: t("error.retry"),
              }
            : undefined
        }
        empty={{ title: t("sessions.empty"), description: t("sessions.emptyDetail") }}
        loadingLabel={t("sessions.loading")}
      />

      <Dialog
        open={pending !== null}
        onClose={() => {
          setPending(null);
        }}
        title={t("sessions.confirmTitle")}
        description={t("sessions.confirmBody")}
        closeLabel={t("sessions.close")}
        footer={
          <>
            <Button
              onClick={() => {
                setPending(null);
              }}
            >
              {t("sessions.cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (pending) void revoke(pending);
              }}
            >
              {t("sessions.confirm")}
            </Button>
          </>
        }
      />
    </Card>
  );
}
